import { ArrowsClockwiseIcon } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';

import { exitReasonKey, isLive, type SelectionSlot } from '@entities/selection';
import { formatObservedDate } from '@entities/vehicle/lib';

import styles from './SelectionHistory.module.css';

interface SelectionHistoryProps {
  history: readonly SelectionSlot[];
}

/**
 * Выбывшие варианты заказа (FE-222, MVP_PRD §34.5, §34.6).
 *
 * **Причина всегда конкретная.** «Объявление снято», «автохаус не ответил»,
 * «вы отклонили» — факты; общего «вариант недоступен» в продукте нет. Коды
 * переводятся через i18n; код без перевода не показывается вовсе.
 *
 * История нужна не для полноты: итоговые заключения строятся по **всем**
 * автомобилям заказа, включая отклонённые самим пользователем — он оплатил всю
 * проделанную работу и должен видеть её целиком (§34.6).
 */
export function SelectionHistory({ history }: SelectionHistoryProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage ?? 'de';

  const exited = history.filter((slot) => !isLive(slot));
  if (exited.length === 0) return null;

  return (
    <section className={styles.section} aria-labelledby="selection-history-title">
      <h2 id="selection-history-title" className={styles.title}>
        {t('selection.history.title', { count: exited.length })}
      </h2>

      <ul className={styles.list}>
        {exited.map((slot) => {
          const reasonKey = slot.exit_reason === null ? null : exitReasonKey(slot.exit_reason);
          const exitedAt =
            slot.exited_at === null ? null : formatObservedDate(slot.exited_at, locale);

          return (
            <li key={slot.selection_id} className={styles.item}>
              <div className={styles.identity}>
                <p className={styles.name}>
                  {slot.vehicle.make} {slot.vehicle.model}
                </p>
                {slot.vehicle.variant === null ? null : (
                  <p className={styles.variant}>{slot.vehicle.variant}</p>
                )}
              </div>

              <p className={styles.reason}>
                {reasonKey !== null && i18n.exists(reasonKey) ? t(reasonKey) : null}
                {exitedAt === null ? null : (
                  <span className={styles.date}>
                    {t('selection.history.at', { date: exitedAt })}
                  </span>
                )}
              </p>

              {slot.replaced_by_selection_id === null ? null : (
                <p className={styles.replaced}>
                  <ArrowsClockwiseIcon size={14} aria-hidden="true" />
                  {t('selection.history.replaced')}
                </p>
              )}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
